import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SEO Component - Her sayfa için benzersiz meta tag ve title yönetimi
 * Optimized for: Title 50-60 chars, Description 140-160 chars
 */
const SEO = ({ 
  title, 
  description, 
  path = '', 
  noindex = false,
  keywords = '',
  ogImage = '/og-image.png',
  ogType = 'website',
  article = null
}) => {
  // Force canonical to https://pilatika.com (no www, no trailing slash except root)
  const baseUrl = 'https://pilatika.com';
  const canonicalPath = path === '/' ? '/' : path.replace(/\/$/, '');
  const fullUrl = `${baseUrl}${canonicalPath}`;
  
  // Title format: "Page Title | Pilatika" (target: 50-60 chars)
  const fullTitle = title ? `${title} | Pilatika` : 'Pilatika - Online Pilates, Yoga ve Wellness Dersleri';
  
  // Default description (target: 140-160 chars)
  const defaultDescription = 'Pilatika ile online pilates, yoga ve wellness dersleri. AI destekli kişisel koçluk, diyetisyen desteği ve günlük motivasyon ile sağlıklı yaşam yolculuğunuza başlayın.';
  const metaDescription = description || defaultDescription;
  
  // Keywords
  const defaultKeywords = 'pilates, yoga, wellness, fitness, online ders, AI koç, diyetisyen, sağlıklı yaşam, egzersiz, meditasyon';
  const metaKeywords = keywords || defaultKeywords;
  
  // OG Image with full URL
  const ogImageUrl = ogImage.startsWith('http') ? ogImage : `${baseUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="author" content="Pilatika" />
      
      {/* Canonical URL - Always https://pilatika.com format */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Robots */}
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} />
      <meta name="googlebot" content={noindex ? 'noindex, follow' : 'index, follow'} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || 'Pilatika'} />
      <meta property="og:site_name" content="Pilatika" />
      <meta property="og:locale" content="tr_TR" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={title || 'Pilatika'} />
      
      {/* Article specific (for blog posts) */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime} />
          <meta property="article:author" content={article.author} />
        </>
      )}
    </Helmet>
  );
};

// SEO Configurations - Optimized titles (50-60 chars) and descriptions (140-160 chars)
export const SEO_CONFIG = {
  home: {
    title: 'Online Pilates, Yoga ve Wellness Dersleri',
    description: 'Pilatika ile online pilates, yoga ve wellness dersleri. AI destekli kişisel koçluk, diyetisyen desteği ve günlük motivasyon ile sağlıklı yaşam yolculuğunuza başlayın.',
    path: '/',
    keywords: 'online pilates, yoga dersleri, wellness, fitness, AI koç, sağlıklı yaşam'
  },
  aiCoach: {
    title: 'AI Wellness Koçu - Kişisel Fitness Rehberi',
    description: 'Yapay zeka destekli kişisel wellness koçunuz ile hedeflerinize ulaşın. Fitness programları, motivasyon desteği ve günlük sağlıklı yaşam önerileri alın.',
    path: '/ai-coach',
    keywords: 'AI koç, yapay zeka wellness, kişisel fitness koçu, motivasyon, sağlıklı yaşam rehberi'
  },
  aiDietitian: {
    title: 'AI Diyetisyen - Kişisel Beslenme Danışmanı',
    description: 'Yapay zeka destekli beslenme danışmanlığı ile kişiselleştirilmiş diyet önerileri alın. Kalori takibi, besin analizi ve sağlıklı beslenme planları.',
    path: '/ai-dietitian',
    keywords: 'AI diyetisyen, yapay zeka beslenme, diyet programı, kalori takibi, sağlıklı beslenme'
  },
  dailyCheckin: {
    title: 'Günlük Wellness Takibi - Sağlık Check-in',
    description: 'Her gün wellness durumunuzu takip edin ve ilerlemenizi görün. Uyku kalitesi, aktivite seviyesi ve ruh hali analizi ile sağlıklı alışkanlıklar edinin.',
    path: '/daily-checkin',
    keywords: 'günlük sağlık takibi, wellness check-in, uyku takibi, aktivite takibi, sağlık günlüğü'
  },
  pricing: {
    title: 'Üyelik Planları ve Fiyatlar',
    description: 'Pilatika üyelik planlarını keşfedin. Ücretsiz, Plus ve Pro seçenekleri ile bütçenize uygun wellness yolculuğunuza hemen başlayın.',
    path: '/pricing',
    keywords: 'pilatika fiyatlar, üyelik planları, premium üyelik, fitness abonelik, wellness paketleri'
  },
  register: {
    title: 'Ücretsiz Üye Ol - Hemen Başla',
    description: 'Pilatika\'ya ücretsiz üye olun ve wellness yolculuğunuza başlayın. AI koç, diyetisyen ve online dersler ile sağlıklı yaşam bir tık uzağınızda.',
    path: '/register',
    keywords: 'pilatika üyelik, ücretsiz kayıt, wellness üyelik, fitness üyelik'
  },
  login: {
    title: 'Giriş Yap',
    description: 'Pilatika hesabınıza giriş yapın ve wellness yolculuğunuza devam edin. Online pilates dersleri ve AI koçunuz sizi bekliyor.',
    path: '/login',
    noindex: true
  },
  becomeInstructor: {
    title: 'Eğitmen Ol - Online Ders Ver ve Kazan',
    description: 'Pilatika eğitmeni olun ve binlerce kullanıcıya ulaşın. Online pilates ve yoga dersleri vererek tutkununuzu gelire dönüştürün.',
    path: '/become-instructor',
    keywords: 'eğitmen ol, online ders ver, pilates eğitmenliği, yoga eğitmeni, fitness koçu'
  },
  about: {
    title: 'Hakkımızda - Pilatika Hikayesi',
    description: 'Pilatika\'nın hikayesini, misyonunu ve vizyonunu keşfedin. Sağlıklı yaşam yolculuğunuzda yanınızda olmak için buradayız.',
    path: '/about',
    keywords: 'pilatika hakkında, şirket hikayesi, misyon vizyon, wellness platform'
  },
  contact: {
    title: 'İletişim - Bize Ulaşın',
    description: 'Pilatika ekibi ile iletişime geçin. Sorularınız, önerileriniz veya iş birliği teklifleriniz için 7/24 yanınızdayız.',
    path: '/contact',
    keywords: 'pilatika iletişim, müşteri hizmetleri, destek, bize ulaşın'
  },
  privacy: {
    title: 'Gizlilik Politikası',
    description: 'Pilatika gizlilik politikası ve kişisel verilerinizin nasıl korunduğu hakkında detaylı bilgi. Güvenliğiniz bizim önceliğimiz.',
    path: '/privacy',
    keywords: 'gizlilik politikası, kişisel veri koruma, güvenlik, pilatika'
  },
  kvkk: {
    title: 'KVKK Aydınlatma Metni',
    description: '6698 Sayılı Kişisel Verilerin Korunması Kanunu kapsamında Pilatika aydınlatma metni. Haklarınız ve veri işleme süreçleri hakkında bilgi.',
    path: '/kvkk',
    keywords: 'KVKK, kişisel veri, aydınlatma metni, veri koruma, yasal'
  },
  refund: {
    title: 'İptal ve İade Koşulları',
    description: 'Pilatika üyelik iptali ve para iadesi koşulları hakkında detaylı bilgi. Şeffaf ve adil iade politikamızı inceleyin.',
    path: '/refund',
    keywords: 'iade politikası, iptal koşulları, para iadesi, üyelik iptali'
  },
  lessons: {
    title: 'Online Dersler - Çok Yakında',
    description: 'Uzman eğitmenlerden online pilates, yoga ve fitness dersleri çok yakında Pilatika\'da. Haberdar olmak için üye olun.',
    path: '/lessons',
    keywords: 'online pilates dersleri, yoga dersleri, fitness dersleri, uzman eğitmenler'
  },
  instructors: {
    title: 'Uzman Eğitmenler - Çok Yakında',
    description: 'Pilates, yoga ve wellness alanında uzman eğitmenler çok yakında Pilatika\'da. Sertifikalı eğitmenlerle tanışmak için takipte kalın.',
    path: '/instructors',
    keywords: 'pilates eğitmeni, yoga eğitmeni, fitness koçu, wellness uzmanı'
  },
  // NOINDEX pages - private user areas
  profile: {
    title: 'Profilim',
    description: 'Pilatika profil sayfanız. Hesap bilgilerinizi ve tercihlerinizi yönetin.',
    path: '/profile',
    noindex: true
  },
  messages: {
    title: 'Mesajlarım',
    description: 'Pilatika mesajlarınız ve bildirimleriniz.',
    path: '/messages',
    noindex: true
  },
  myCourses: {
    title: 'Kurslarım',
    description: 'Satın aldığınız ve kayıtlı olduğunuz kurslar.',
    path: '/my-courses',
    noindex: true
  },
  instructorDashboard: {
    title: 'Eğitmen Paneli',
    description: 'Eğitmen kontrol paneli ve ders yönetimi.',
    path: '/instructor-dashboard',
    noindex: true
  },
  admin: {
    title: 'Admin Panel',
    description: 'Pilatika yönetim paneli.',
    path: '/admin',
    noindex: true
  },
  paymentSuccess: {
    title: 'Ödeme Başarılı',
    description: 'Ödemeniz başarıyla tamamlandı.',
    path: '/payment-success',
    noindex: true
  },
  paymentFailed: {
    title: 'Ödeme Başarısız',
    description: 'Ödeme işlemi başarısız oldu.',
    path: '/payment-failed',
    noindex: true
  }
};

export default SEO;
